/* eslint-disable @typescript-eslint/no-shadow */
import { storyblokEditable } from '@storyblok/react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { FC, ReactNode, useEffect, useRef } from 'react';

import Button from '@/components/elements/Button';
import ButtonArrow from '@/components/elements/ButtonArrow';
import Container from '@/components/elements/Container';
import ExpandingButton from '@/components/elements/ExpandingButton';
import Heading from '@/components/elements/Heading';
import PreHeading from '@/components/elements/PreHeading';
import Prose from '@/components/elements/Prose';
import Section from '@/components/elements/Section';
import Text from '@/components/elements/Text';
import TopographicalVideoMap from '@/components/elements/TopoMap/TopographicalVideoMap';
import { storyblokLink } from '@/helpers/storyblokLink';
import { useStore } from '@/lib/store';
import { getStoryblokImageUrl } from '@/lib/utils';

export interface HeroHomeProps {
  children?: ReactNode;
  story: any;
  blok: any;
  pre_heading?: string;
  heading?: string;
  content?: string;
  services?: object;
}

const HeroHome: FC<HeroHomeProps> = ({
  children,
  blok,
  pre_heading = blok.hero_pre_heading,
  heading = blok.hero_heading,
  content = blok.hero_content,
  services = blok.hero_services,
}) => {
  const div = useRef(null);
  const setHeaderLight = useStore(({ setHeaderLight }) => setHeaderLight);

  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.35,
      },
    },
  };

  const solutionContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        delay: 0.5,
        duration: 0.25,
        staggerChildren: 0.25,
      },
    },
  };

  const solution = {
    hidden: { opacity: 0, y: 35 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 75,
        mass: 0.75,
        duration: 0.25,
      },
    },
    hover: {
      flex: '1.5 1 0%',
      transition: {
        type: 'spring',
        stiffness: 100,
        mass: 0.5,
        duration: 0.25,
      },
    },
    exit: {
      flex: '1 1 0%',
    },
  };

  const solutionBackground = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.35,
        delay: 0.5,
      },
    },
  };

  useEffect(() => {
    setHeaderLight(false);
  }, []);

  return (
    <Section
      className="dark relative bg-theme-black"
      {...storyblokEditable(blok)}
    >
      <div className="absolute top-0 size-full">
        <TopographicalVideoMap video={blok.background[0]?.filename} />
      </div>
      {children}
      <div ref={div} className="absolute left-1/2 top-0 -translate-x-1/2"></div>
      <Container size="widest" className="relative z-40">
        <div className="flex max-h-[1000px] items-center md:h-[calc(80vh-var(--notification-height))]">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={container}
            className="max-w-3xl space-y-12 py-12 sm:py-16 md:py-24 lg:py-36"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <PreHeading element="h1" color="white" size="lg">
                  {pre_heading}
                </PreHeading>
                <Heading
                  element={blok.pre_heading ? 'h2' : 'h1'}
                  color="white"
                  weight="semi"
                  size="2xl"
                >
                  {heading}
                </Heading>
              </div>
              {content && (
                <div>
                  <Prose size={'lg'} content={content}></Prose>
                  {/* <Text size="xl" color="white" className="font-normal">
                    {content}
                  </Text> */}
                </div>
              )}
            </div>
            {blok.CTAs?.length && (
              <div className="flex flex-wrap gap-4">
                {blok.CTAs.map((b) => (
                  <Button {...b} key={b._uid}>
                    <ButtonArrow
                      key={b._uid}
                      className={`${b.style} -ml-1 mr-1 h-5 w-6`}
                    />
                    {b.label}
                  </Button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
        <h2 className="sr-only">Services</h2>
        <div className="relative">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={solutionContainer}
            className="relative z-20 mx-0 flex h-[1200px] flex-col gap-[2px] md:-mx-8 lg:-mx-10 lg:h-[410px] lg:flex-row xl:-mx-16"
          >
            {services.map((service) => (
              <motion.div
                key={service._uid}
                style={{ flex: '1 1 0%' }}
                variants={solution}
                whileHover="hover"
                exit="exit"
                className="group relative z-10 flex hover:z-20"
              >
                <Link
                  href={storyblokLink(service.service)}
                  className="block overflow-hidden"
                  onMouseOver={
                    service.video?.filename
                      ? (event) =>
                          event.currentTarget.querySelector('video').play()
                      : null
                  }
                  onMouseOut={
                    service.video?.filename
                      ? (event) =>
                          event.currentTarget.querySelector('video').pause()
                      : null
                  }
                >
                  <div className="absolute z-10 lg:w-96">
                    <div className="space-y-4 p-8 pr-16 xl:p-12">
                      <Heading
                        element="h3"
                        color="white"
                        size="2xs"
                        className="font-semibold lg:max-w-[12ch]"
                      >
                        {service.heading
                          ? service.heading
                          : service.service.story.name}
                      </Heading>
                      <div className="space-y-8 duration-500 ease-smooth group-hover:opacity-100 lg:opacity-0 2xl:opacity-100">
                        <Text size="md" color="white">
                          {service.content}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <div className="absolute inset-0 bg-theme-darkgrey duration-500 ease-smooth group-hover:scale-[1.025]">
                    <div className="absolute inset-0 shadow-xl duration-500 ease-smooth"></div>
                    <div className="absolute inset-0 overflow-hidden opacity-0 duration-500 ease-smooth group-hover:opacity-100">
                      {service.video?.filename && (
                        <video
                          src={service.video?.filename}
                          className="absolute inset-0 size-full object-cover"
                          width="100%"
                          height="100%"
                          muted
                          loop
                        />
                      )}
                      <Image
                        alt={service.image?.alt}
                        src={getStoryblokImageUrl(
                          service.image?.filename,
                          1200,
                          0
                        )}
                        fill
                        sizes="(max-width: 768px) 100vw,
                  (max-width: 768px) 25vw,
                  25vw"
                        className={`absolute inset-0 object-cover duration-500 ease-smooth ${
                          service.video?.filename
                            ? 'group-hover:scale-110 group-hover:opacity-0 group-hover:duration-700'
                            : 'group-hover:scale-110 group-hover:duration-[10s]'
                        }`}
                      />
                    </div>
                    <div className="absolute -inset-px bg-gradient-to-l from-theme-darkgrey/0 to-theme-darkgrey/90 opacity-0 duration-500 ease-smooth group-hover:opacity-80"></div>
                  </div>
                  <ExpandingButton
                    link={service.service}
                    className="absolute bottom-10 right-10 size-[52px] duration-1000 ease-smooth group-hover:w-52"
                  >
                    Learn More
                  </ExpandingButton>
                </Link>
              </motion.div>
            ))}
            <motion.div
              variants={solutionBackground}
              className="absolute inset-0 z-0 bg-theme-charcoal"
            ></motion.div>
          </motion.div>
        </div>
      </Container>
      <div className="absolute inset-x-0 bottom-0 h-1/4 bg-theme-lightgrey md:h-[205px]"></div>
    </Section>
  );
};

export default HeroHome;
